<template>
  <div>
    <header-component />
    <div class="container-fluid mt--6">
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h3 class="mb-0">Customer List</h3>
        </div>
        <el-table
          :data="tableData"
          class="table-responsive table-flush"
          header-row-class-name="thead-light"
        >
          <el-table-column
            label="Name"
            prop="name"
            min-width="160">
            <table-filter-component
              slot="header"
              slot-scope="scope"
              :visible-filters="visibleFilters"
              :scope="scope"
              v-model="filters"
            />
            <template slot-scope="scope">
              <span
                v-if="scope.row.lastName"
              >{{ scope.row.firstName | capitalize }} {{ scope.row.lastName | capitalize }}
              <span v-if="scope.row.lastNameMarathi != ''">({{ scope.row.firstNameMarathi }} {{ scope.row.lastNameMarathi }})</span></span>
              <span v-else>{{ scope.row.firstName | capitalize }} <span v-if="scope.row.firstNameMarathi != ''">({{ scope.row.firstNameMarathi }})</span></span>
            </template>
          </el-table-column>

          <el-table-column
            label="Phone"
            prop="phone"
            min-width="125">
            <table-filter-component
              slot="header"
              slot-scope="scope"
              :visible-filters="visibleFilters"
              :scope="scope"
              v-model="filters"
            />
            <template slot-scope="scope">
              <span style="white-space:nowrap;">{{ scope.row.phone }}</span>
            </template>
          </el-table-column>

          <el-table-column
            label="Locality"
            prop="locality"
            min-width="120"
            show-overflow-tooltip>
            <table-filter-component
              slot="header"
              slot-scope="scope"
              :visible-filters="visibleFilters"
              :scope="scope"
              v-model="filters"
            />
            <template slot-scope="scope">{{ scope.row.locality }}</template>
          </el-table-column>

          <el-table-column label="Role" min-width="110">
            <template slot-scope="scope">
              <span
                v-if="scope.row.role && scope.row.role.label"
                class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
              >{{ scope.row.role.label }}</span>
              <span
                v-else
                class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700"
              >Unassigned</span>
            </template>
          </el-table-column>

          <el-table-column label="Linked Branch" min-width="120">
            <template slot-scope="scope">
              <span v-if="scope.row.customerTypes === 'branch' && scope.row.branchId">
                <span
                  class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full"
                  :style="branchBadgeStyle(scope.row.branchId)"
                >
                  {{ branchName(scope.row.branchId) }}
                </span>
              </span>
            </template>
          </el-table-column>

          <el-table-column min-width="100">
            <template slot-scope="scope">
              <el-dropdown
                slot="dropdown"
                @command="command => handleAction(scope.row, command)">
                <el-button type="primary" size="mini">
                  Actions
                  <i class="el-icon-arrow-down el-icon--right" />
                </el-button>
                <el-dropdown-menu slot="dropdown" class="dropdown-menu dropdown-menu-arrow show">
                  <el-dropdown-item command="edit">Edit</el-dropdown-item>
                  <el-dropdown-item command="ledger">View Ledger</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>

          <template slot="empty">
            <div class="table-loader"/>
          </template>
        </el-table>

        <div class="card-footer">
          <div
            v-if="meta"
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          >
            <div class>
              <p class="card-category">
                Showing {{ meta.from + 1 }} to {{ meta.to }} of {{ meta.total }} entries
                <span v-if="selectedRows.length">&nbsp; &nbsp; {{ selectedRows.length }} rows selected</span>
              </p>
            </div>
            <pagination :meta="meta" @paginate="setPagination" />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import {
  Select,
  Option,
  DatePicker,
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Dialog,
  Button,
} from "element-ui";
import { mapGetters, mapActions } from "vuex";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";
import headerComponent from "../Components/HeaderComponent";
import pagination from "@/components/AmsaPagination";
import tableFilter from "@/components/TableFilterComponent";

export default {
  filters: {
    capitalize(value) {
      if (!value) return '';
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    "header-component": headerComponent,
    pagination,
    "table-filter-component": tableFilter,
  },
  data: () => {
    return {
      pageSize: 50,
      selectedRows: [],
      filters: {},
      visibleFilters: [],
      allBranches: [],
    };
  },

  computed: {
    ...mapGetters({
      userList: "user/getUserList",
      user: "user/getUser",
    }),
    tableData() {
      return this.userList.rows;
    },
    meta() {
      return this.userList.meta;
    },
  },

  watch: {
    filters: {
      deep: true,
      handler: function (value) {
        Object.filter = (obj, predicate) =>
          Object.keys(obj)
            .filter((key) => predicate(obj[key]))
            .reduce((res, key) => ((res[key] = obj[key]), res), {});

        const filtered = Object.filter(value, (i) => i.length > 2);
        if (Object.entries(filtered).length > 0) {
          this.paginate(1, filtered);
        } else {
          this.paginate();
        }
      },
    },
  },

  beforeMount() {
    this.fetchAllBranches();
    this.paginate();
  },

  methods: {
    ...mapActions({ getUsers: "user/getUsers", fetchUser: "user/fetchUser" }),

    fetchAllBranches() {
      apiHelper
        .call("get", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`)
        .then((res) => { this.allBranches = res.data.data || res.data; })
        .catch(() => {});
    },

    branchName(branchId) {
      var b = this.allBranches.find(function(x) { return x.id === branchId; });
      return b ? b.name : branchId;
    },
    branchBadgeStyle(branchId) {
      var b = this.allBranches.find(function(x) { return x.id === branchId; });
      var color = b && b.color;
      if (!color) return { background: '#dbeafe', color: '#1e40af' };
      return { background: color, color: '#fff' };
    },

    handleAction(user, command) {
      if (command === "edit") {
        this.fetchUser(user.userId).then((res) => {
          if (res) this.$router.push("/users/add/" + user.userId);
        });
      } else if (command === "ledger") {
        this.$router.push("/customers/" + user.userId + "/ledger");
      }
    },

    setPagination(data) {
      this.pageSize = data.perPage;
      this.paginate(data.page, this.filters);
    },

    paginate(page = 1, filters = {}) {
      let query = { page, perPage: this.pageSize, ...filters, role: 'Customer' };
      this.getUsers(query);
    },
  },
};
</script>
